import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "../lib/api";
import Topbar from "../components/Topbar";
import PizzaSpinner from "../components/PizzaSpinner";
import PriceChangeRequestModal from "../components/PriceChangeRequestModal";
import { useAuth } from "../lib/auth";
import { Tag, Pencil, Package, Info } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function SupplierPrices() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rels, setRels] = useState(null);
  const [proposing, setProposing] = useState(null);

  const sid = user?.supplier_id;

  const load = async () => {
    if (!sid) return;
    const r = await api.get(`/suppliers/${sid}/supplies`, { params: { active_only: true, include_supply_details: true } });
    setRels(r.data);
  };

  useEffect(() => { load(); }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rels) return <><Topbar title={t("supplierPrices.title")} /><PizzaSpinner /></>;

  return (
    <>
      <Topbar title={t("supplierPrices.title")} subtitle={t("supplierPrices.subtitle")} />
      <div className="p-6 space-y-5">
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] rounded-2xl p-5 text-white">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center flex-shrink-0">
              <Tag className="w-5 h-5 text-[#1A1A1A]" />
            </div>
            <div>
              <h3 className="font-display font-bold text-base">{t("supplierPrices.introTitle")}</h3>
              <p className="text-xs text-slate-300 mt-1 leading-relaxed max-w-2xl">{t("supplierPrices.introBody")}</p>
            </div>
          </div>
        </div>

        <div data-testid="supplier-prices-approval-note" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">{t("supplierPrices.approvalNoteTitle")}</div>
            <p className="text-xs mt-0.5">{t("supplierPrices.approvalNoteBody")}</p>
          </div>
        </div>

        {rels.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Package className="w-7 h-7 text-slate-400" />
            </div>
            <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{t("supplierPrices.emptyTitle")}</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{t("supplierPrices.emptyMsg")}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.supply")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.category")}</th>
                  <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierPrices.currentPrice")}</th>
                  <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.minOrderQty")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierPrices.lastUpdate")}</th>
                  <th className="px-6 py-3 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierPrices.action")}</th>
                </tr>
              </thead>
              <tbody>
                {rels.map((r, idx) => (
                  <motion.tr key={r.supply_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                    data-testid={`my-product-row-${r.supply_id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-6 py-3.5">
                      <div className="text-sm font-semibold text-[#1A1A1A]">{r.supply?.name}</div>
                      <div className="text-[11px] text-slate-500 capitalize">{r.supply?.unit}{r.supply?.presentation ? ` · ${r.supply.presentation}` : ""}</div>
                    </td>
                    <td className="px-6 py-3.5 text-xs text-slate-500 capitalize">{r.supply?.category}</td>
                    <td className="px-6 py-3.5 text-right">
                      <div className="text-base font-display font-extrabold font-mono">{fmtMXN(r.unit_price)}</div>
                    </td>
                    <td className="px-6 py-3.5 text-right text-sm font-mono text-slate-600">{r.min_order_qty ?? "—"}</td>
                    <td className="px-6 py-3.5 text-xs text-slate-500">{fmtDate(r.last_price_update || r.updated_at)}</td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center justify-end">
                        <button data-testid={`my-product-propose-${r.supply_id}`} onClick={() => setProposing(r)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A1A] hover:bg-black text-white rounded-lg text-xs font-bold">
                          <Pencil className="w-3 h-3" /> {t("supplierPrices.proposeChange")}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {proposing && (
        <PriceChangeRequestModal
          supply={{ ...proposing, supply_name: proposing.supply?.name }}
          onClose={() => setProposing(null)}
          onSuccess={load}
        />
      )}
    </>
  );
}
